// ThreadV5DagExplorer — the UNIFIED Thread-V5 DAG system (FXC DAG-viz redesign).
//
// One component, ONE selection model, coordinated views. It replaces the three
// fragmented surfaces that each shipped a different layout engine:
//   • ThreadV5Provenance / ThreadV5DagMinimap  (the stage-rollup tile)
//   • ThreadV5DagFocus                          (the ELK trail / full toggle)
//   • ThreadV5LineageExplorer                   (the sigma full-page surface)
//
// The design (Claude brief + /dag_design prototype) is: a single `selectedId`
// drives every view at once. The TRIMMED CORE shipped spine/cone/map + a basic
// trace lens. This file ports the FULL prototype:
//
//   1. SPINE  — an always-on orienter: ordered pipeline STAGES, size ∝ count.
//   2. GRAPH  — the PRIMARY "where did this come from" view as the design draws
//               it: HTML-positioned nodes laid out by one of SIX layouts
//               (tb/bt/lr/rl/force/radial), edges following, with the active
//               cone LENS (trace/impact/diff/filter/health/versions) lighting
//               roles, time-travel revealing the build, and version badges. The
//               layout switch + view flips ANIMATE via dagLayouts' morph helper.
//   3. CONE   — the collapsed-chain reading of the same lineage (run-fans fold to
//               ×N super-nodes). A legible fallback that scales O(path).
//   4. MAP    — the whole graph as a density/cluster fallback, lineage lit.
//   5. BRANCH — the git-style branch/commit graph + A/B compare + merge preview.
//
// The analysis "lenses" (trace / impact / diff / filter / health / versions) are
// MODES on the graph/cone, not separate screens — wired to dagLenses + dagAux +
// the DagVersionsPanel / DagBranchPanel feature modules. The auxiliary controls
// (search, health, export/copy-link, rebuild, time scrubber, trace direction,
// run-grid drill-in) are placed per the design and only shown in full (expand);
// compact (tile) stays minimal.
//
// It is PURE on its props (nodes/edges already fetched) so the same component
// mounts in the tile (compact) AND the expand (full). It does NOT fetch.
//
// Colours come from --v5-* design tokens (see thread-v5.css) — never hard-coded.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import {
  ancestors,
  buildIndex,
  descendants,
  fromProvenanceGraph,
} from "@flexcompute/thread-graph";
import type { Graph, GraphIndex, GraphNode } from "@flexcompute/thread-graph";
import { Icons } from "../thread-v4/icons";

import {
  LAYOUT_MODES,
  createLayoutMorph,
  layout as computeLayout,
  type LayoutMode,
  type Positions,
} from "./dagLayouts";
import { applyLens, type DagLensMode, type NodeRole } from "./dagLenses";
import {
  CopyLinkButton,
  DagSearchBox,
  DownloadConeButton,
  HealthPanel,
  RebuildPlanPanel,
  RunGridDrillIn,
  TimeTravelScrubber,
  TraceDirectionToggle,
  buildDeepLinkHash,
  healthSummary,
  parseDeepLinkHash,
  statusOf,
  timeTravel,
  type TraceDirection,
} from "./dagAux";
import { DagVersionsPanel, deterministicVersionsFor, type VerView } from "./DagVersionsPanel";
import { DagBranchPanel, type BranchInput, type CommitInput } from "./DagBranchPanel";
import { DagScaleView } from "./DagScaleView";
import { buildRealModel } from "./dagScaleModel";

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/** Minimal provenance node shape (matches fetchLineage → DAGData.nodes). */
export interface DagExplorerNode {
  id: string;
  kind?: string;
  type?: string;
  label?: string;
  name?: string;
  path?: string;
  value_json?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Minimal provenance edge shape (matches fetchLineage → DAGData.edges). */
export interface DagExplorerEdge {
  from?: string;
  to?: string;
  source?: string;
  target?: string;
  parent_id?: string;
  child_id?: string;
  type?: string;
  relationship?: string;
  operation?: string;
  [key: string]: unknown;
}

/** The selectable views. `graph` is the design's primary animated layout view. */
export type DagView = "spine" | "graph" | "cone" | "map" | "scale" | "branch";

/** The cone analysis lenses (the prototype's `coneMode`, full superset). */
export type DagLens = "trace" | "impact" | "diff" | "filter" | "health" | "versions";

export interface ThreadV5DagExplorerProps {
  /** The real provenance graph (already fetched). The component never fetches. */
  nodes: DagExplorerNode[];
  edges: DagExplorerEdge[];
  /**
   * The SINGLE selection model — the EXPLORER's own root. A single click on any
   * node RE-ROOTS the lineage cone/graph (this internal root) and STAYS in the
   * DAG; it never navigates away. `selectedId` SEEDS the root on mount; after the
   * first in-DAG re-root the explorer self-manages so a stale controlled value
   * can't yank the root back out from under the user.
   */
  selectedId?: string | null;
  /**
   * A PURE, non-navigating selection notifier. Fired on every single-click
   * re-root so a host can mirror the selection (e.g. highlight a row) WITHOUT
   * closing the DAG or switching surfaces. Must NOT navigate — use `onActivate`
   * for the deliberate "jump to this row" action.
   */
  onSelect?: (id: string) => void;
  /**
   * The DELIBERATE "open this row / jump to the table" action — wired to a
   * DOUBLE-CLICK on a node and to the explicit "open row ↗" affordance on the
   * selected node. This is the only path that may navigate the host away from the
   * DAG (e.g. close the focus overlay and switch to the run table).
   */
  onActivate?: (id: string) => void;
  /** Active view. Controlled when `onViewChange` is provided. */
  view?: DagView;
  onViewChange?: (view: DagView) => void;
  /**
   * compact=true → the TILE: default view 'spine', minimal chrome, clicking a
   * node still re-roots. compact=false → the EXPAND: default view 'cone', a calm
   * two-row toolbar (view switch + lens bar + search) with the power controls
   * tucked behind an "Advanced ⋯" disclosure. Default false.
   */
  compact?: boolean;
  /** The cone analysis lens. Controlled when `onLensChange` is provided. */
  lens?: DagLens;
  onLensChange?: (lens: DagLens) => void;
  /** Optional class on the root for layout. */
  className?: string;
  /** Tile-only: when compact, renders an expand control that calls this. */
  onExpand?: () => void;
  /**
   * Optional REAL CoW branches for the Versions/Branch views. When omitted, a
   * deterministic-from-id presentation is used so the views still render.
   */
  branchHistory?: BranchInput[];
  /** Real commits paired with `branchHistory`. */
  commits?: CommitInput[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage rollup (ported from ThreadV5Provenance.buildRollup) — drives the SPINE.
// Operations/scripts are the transforms BETWEEN stages (the arrows), so they're
// excluded from the rollup and reported separately.
// ─────────────────────────────────────────────────────────────────────────────

type Tone = "ink" | "blue" | "green" | "amber";

interface StageDef {
  tone: Tone;
  label: string;
  match: (kind: string) => boolean;
}

const STAGE_DEFS: StageDef[] = [
  { tone: "ink", label: "inputs", match: (k) => /geometr|source|param|dataset|config|airfoil/.test(k) },
  { tone: "blue", label: "surface mesh", match: (k) => k === "surface_mesh" },
  { tone: "blue", label: "volume mesh", match: (k) => k === "volume_mesh" || (k.includes("mesh") && k !== "surface_mesh") },
  { tone: "green", label: "runs", match: (k) => /\brun|case|solve|simulation|sample/.test(k) },
  { tone: "amber", label: "data", match: (k) => /quantity|dataframe|value|coefficient|matrix|derivative/.test(k) },
  { tone: "amber", label: "plots", match: (k) => /plot|figure|chart|report|deck|slide|artifact|view|envelope/.test(k) },
];

// Compute steps (the arrows between artifacts) are excluded from the SPINE stage
// rollup. 'function' covers v1-seeded operation nodes (the v1 nodes.kind CHECK has
// no 'operation', so seeds map operation→function). The cone still shows them — it
// doesn't use isTransform — so the scripts stay visible in the lineage.
const isTransform = (kind: string): boolean => /operation|script|\bop\b|transform|function/.test(kind);

function stageOf(kind: string): number {
  const k = (kind || "").toLowerCase();
  for (let i = 0; i < STAGE_DEFS.length; i += 1) if (STAGE_DEFS[i]!.match(k)) return i;
  return STAGE_DEFS.length - 1;
}

interface Stage {
  idx: number;
  tone: Tone;
  label: string;
  count: number;
  here: boolean;
  gap: boolean;
}

interface Rollup {
  stages: Stage[];
  totalNodes: number;
  transforms: number;
  runCount: number;
  focusStage: number | null;
}

const EMPTY_ROLLUP: Rollup = { stages: [], totalNodes: 0, transforms: 0, runCount: 0, focusStage: null };

function buildRollup(graph: Graph, index: GraphIndex, selectedId: string | null): Rollup {
  if (graph.nodes.length === 0) return EMPTY_ROLLUP;

  const hasEdge = new Set<string>();
  for (const e of graph.edges) {
    hasEdge.add(e.source);
    hasEdge.add(e.target);
  }

  const agg = new Map<number, { count: number; connected: number; runs: number }>();
  let transforms = 0;
  for (const node of graph.nodes) {
    const kind = node.type ?? "";
    if (isTransform(kind)) {
      transforms += 1;
      continue;
    }
    const s = stageOf(kind);
    const entry = agg.get(s) ?? { count: 0, connected: 0, runs: 0 };
    entry.count += 1;
    if (hasEdge.has(node.id)) entry.connected += 1;
    if (/run|case|sample/.test(kind.toLowerCase())) entry.runs += 1;
    agg.set(s, entry);
  }

  // Which stage holds the selection (light it). selectedId may be a synthetic
  // row id, not a graph node — fall back to the runs stage.
  let focusStage: number | null = null;
  if (selectedId) {
    const n = index.nodeById.get(selectedId);
    if (n && !isTransform(n.type ?? "")) focusStage = stageOf(n.type ?? "");
  }
  if (focusStage === null) {
    const runsIdx = STAGE_DEFS.findIndex((d) => d.label === "runs");
    if (agg.has(runsIdx)) focusStage = runsIdx;
  }

  const stages: Stage[] = [...agg.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([idx, e]) => ({
      idx,
      tone: STAGE_DEFS[idx]!.tone,
      label: STAGE_DEFS[idx]!.label,
      count: e.count,
      here: idx === focusStage,
      gap: e.connected === 0,
    }));

  const runCount = [...agg.values()].reduce((s, e) => s + e.runs, 0);
  return { stages, totalNodes: graph.nodes.length, transforms, runCount, focusStage };
}

// ─────────────────────────────────────────────────────────────────────────────
// Default root: the most-DOWNSTREAM artifact (a plot/output with no children) —
// "where did this come from". Rooted at the SINK.
// ─────────────────────────────────────────────────────────────────────────────

function pickDefaultRoot(graph: Graph, index: GraphIndex): string {
  if (graph.nodes.length === 0) return "";
  let bestSink = "";
  let bestSinkScore = -1;
  let bestAny = graph.nodes[0]!.id;
  let bestAnyDeg = -1;
  for (const n of graph.nodes) {
    const outDeg = index.successors.get(n.id)?.length ?? 0;
    const inDeg = index.predecessors.get(n.id)?.length ?? 0;
    if (inDeg + outDeg > bestAnyDeg) {
      bestAnyDeg = inDeg + outDeg;
      bestAny = n.id;
    }
    if (outDeg === 0 && inDeg > 0) {
      const isPlot = /plot|figure|chart|report|view|envelope/.test((n.type ?? "").toLowerCase());
      const score = inDeg + (isPlot ? 1000 : 0);
      if (score > bestSinkScore) {
        bestSinkScore = score;
        bestSink = n.id;
      }
    }
  }
  return bestSink || bestAny;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONE model — the collapsed-chain reading.
// ─────────────────────────────────────────────────────────────────────────────

/** Min siblings before a same-kind group folds into a ×N super-node. */
const FAN_COLLAPSE_MIN = 3;
/** Hard cap on individual nodes drawn per layer when a fan is expanded. */
const LAYER_RENDER_CAP = 60;

interface ConeNodeReal {
  kind: "real";
  id: string;
  label: string;
  type: string;
  depth: number;
}

interface ConeNodeFan {
  kind: "fan";
  id: string;
  type: string;
  label: string;
  count: number;
  memberIds: string[];
  depth: number;
}

type ConeNode = ConeNodeReal | ConeNodeFan;

interface ConeLayer {
  depth: number;
  nodes: ConeNode[];
}

interface ConeModel {
  root: GraphNode | null;
  up: ConeLayer[];
  down: ConeLayer[];
  totalUp: number;
  totalDown: number;
}

const fanIdFor = (parentDepth: number, type: string, signature: string): string =>
  `fan:${parentDepth}:${type}:${signature}`;

function buildConeDirection(
  index: GraphIndex,
  rootId: string,
  direction: "up" | "down",
  expanded: ReadonlySet<string>,
): { layers: ConeLayer[]; total: number } {
  const step = direction === "up" ? index.predecessors : index.successors;
  const layers: ConeLayer[] = [];
  let frontier: string[] = [rootId];
  const seen = new Set<string>([rootId]);
  let total = 0;
  for (let depth = 1; depth <= 32 && frontier.length > 0; depth += 1) {
    const raw: string[] = [];
    for (const f of frontier) {
      for (const nb of step.get(f) ?? []) {
        if (!seen.has(nb)) {
          seen.add(nb);
          raw.push(nb);
        }
      }
    }
    if (raw.length === 0) break;
    total += raw.length;

    const byType = new Map<string, string[]>();
    for (const id of raw) {
      const t = index.nodeById.get(id)?.type ?? "node";
      const arr = byType.get(t) ?? [];
      arr.push(id);
      byType.set(t, arr);
    }

    const nodes: ConeNode[] = [];
    const nextFrontier: string[] = [];
    for (const [type, ids] of byType) {
      const fid = fanIdFor(depth, type, String(ids.length));
      const shouldFold = ids.length >= FAN_COLLAPSE_MIN && !expanded.has(fid);
      if (shouldFold) {
        const label = index.nodeById.get(ids[0]!)?.label ?? type;
        nodes.push({
          kind: "fan",
          id: fid,
          type,
          label: `${label} ×${ids.length}`,
          count: ids.length,
          memberIds: ids,
          depth,
        });
        nextFrontier.push(ids[0]!);
      } else {
        const drawn = ids.slice(0, LAYER_RENDER_CAP);
        for (const id of drawn) {
          const n = index.nodeById.get(id)!;
          nodes.push({ kind: "real", id, label: n.label, type: n.type, depth });
          nextFrontier.push(id);
        }
        if (ids.length > drawn.length) {
          const rest = ids.slice(LAYER_RENDER_CAP);
          nodes.push({
            kind: "fan",
            id: `${fid}:overflow`,
            type,
            label: `+${rest.length} more`,
            count: rest.length,
            memberIds: rest,
            depth,
          });
        }
      }
    }
    layers.push({ depth, nodes });
    frontier = nextFrontier;
  }
  return { layers, total };
}

function buildCone(
  index: GraphIndex,
  rootId: string,
  showDownstream: boolean,
  expanded: ReadonlySet<string>,
): ConeModel {
  const root = index.nodeById.get(rootId) ?? null;
  if (!root) return { root: null, up: [], down: [], totalUp: 0, totalDown: 0 };
  const upBuilt = buildConeDirection(index, rootId, "up", expanded);
  const downBuilt = showDownstream
    ? buildConeDirection(index, rootId, "down", expanded)
    : { layers: [], total: 0 };
  return {
    root,
    up: upBuilt.layers,
    down: downBuilt.layers,
    totalUp: upBuilt.total,
    totalDown: downBuilt.total,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// MAP model — the whole graph laid out, lineage lit.
// ─────────────────────────────────────────────────────────────────────────────

interface MapPoint {
  id: string;
  x: number;
  y: number;
  lit: boolean;
  isRoot: boolean;
  tone: Tone;
}

interface MapModel {
  points: MapPoint[];
  litEdges: Array<{ ax: number; ay: number; bx: number; by: number }>;
  litCount: number;
}

function buildMap(graph: Graph, index: GraphIndex, rootId: string): MapModel {
  const n = graph.nodes.length;
  if (n === 0) return { points: [], litEdges: [], litCount: 0 };

  const indeg = new Map<string, number>();
  for (const node of graph.nodes) indeg.set(node.id, index.predecessors.get(node.id)?.length ?? 0);
  const depth = new Map<string, number>();
  const queue: string[] = [];
  for (const node of graph.nodes) {
    if ((indeg.get(node.id) ?? 0) === 0) {
      depth.set(node.id, 0);
      queue.push(node.id);
    }
  }
  for (let qi = 0; qi < queue.length; qi += 1) {
    const u = queue[qi]!;
    const du = depth.get(u) ?? 0;
    for (const v of index.successors.get(u) ?? []) {
      if (du + 1 > (depth.get(v) ?? -1)) depth.set(v, du + 1);
      const d = (indeg.get(v) ?? 0) - 1;
      indeg.set(v, d);
      if (d === 0) queue.push(v);
    }
  }
  let maxDepth = 0;
  for (const d of depth.values()) maxDepth = Math.max(maxDepth, d);
  if (maxDepth === 0) maxDepth = 1;

  const lit = new Set<string>([rootId]);
  if (index.nodeById.has(rootId)) {
    for (const id of ancestors(index, rootId, 32).ids) lit.add(id);
    for (const id of descendants(index, rootId, 32).ids) lit.add(id);
  }

  const byRank = new Map<number, string[]>();
  for (const node of graph.nodes) {
    const r = depth.get(node.id) ?? 0;
    const arr = byRank.get(r) ?? [];
    arr.push(node.id);
    byRank.set(r, arr);
  }
  const pos = new Map<string, { x: number; y: number }>();
  const points: MapPoint[] = [];
  for (const [r, ids] of byRank) {
    const x = 0.06 + (r / maxDepth) * 0.88;
    const m = ids.length;
    ids.forEach((id, i) => {
      const y = m === 1 ? 0.5 : 0.06 + (i / (m - 1)) * 0.88;
      pos.set(id, { x, y });
      const node = index.nodeById.get(id)!;
      points.push({
        id,
        x,
        y,
        lit: lit.has(id),
        isRoot: id === rootId,
        tone: STAGE_DEFS[stageOf(node.type ?? "")]!.tone,
      });
    });
  }

  const litEdges: MapModel["litEdges"] = [];
  for (const e of graph.edges) {
    if (lit.has(e.source) && lit.has(e.target)) {
      const a = pos.get(e.source);
      const b = pos.get(e.target);
      if (a && b) litEdges.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y });
    }
  }

  return { points, litEdges, litCount: lit.size };
}

// ─────────────────────────────────────────────────────────────────────────────
// FIT-TO-FILL — every laid-out view (graph/map/cone/spine) must use the FULL
// panel, not sit tiny in a corner. Each view's content lives in normalized [0,1]
// coords; `fitNormalized` rescales a set of points so their bounding box fills
// [pad, 1-pad] in BOTH axes independently (so the content grows to use the full
// width AND height). A degenerate (zero-width/height) span is centred, not
// divided by zero. The result is a remap function applied per point/position.
// ─────────────────────────────────────────────────────────────────────────────

interface FitFn {
  x(v: number): number;
  y(v: number): number;
}

/** Build an axis remap that maps the bbox of `pts` onto [pad, 1-pad] in x and y. */
function fitNormalized(pts: Array<{ x: number; y: number }>, pad = 0.06): FitFn {
  if (pts.length === 0) return { x: (v) => v, y: (v) => v };
  let mnx = Infinity;
  let mxx = -Infinity;
  let mny = Infinity;
  let mxy = -Infinity;
  for (const p of pts) {
    if (p.x < mnx) mnx = p.x;
    if (p.x > mxx) mxx = p.x;
    if (p.y < mny) mny = p.y;
    if (p.y > mxy) mxy = p.y;
  }
  const span = 1 - 2 * pad;
  const sx = mxx - mnx;
  const sy = mxy - mny;
  const remap = (v: number, mn: number, s: number): number =>
    s < 1e-6 ? 0.5 : pad + (span * (v - mn)) / s;
  return {
    x: (v) => remap(v, mnx, sx),
    y: (v) => remap(v, mny, sy),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Small presentation helpers
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (n: number): string => n.toLocaleString();

const TONE_CLASS: Record<Tone, string> = {
  ink: "v5-bundle-tone-ink",
  blue: "v5-bundle-tone-blue",
  green: "v5-bundle-tone-green",
  amber: "v5-bundle-tone-amber",
};

const VIEW_DEFS: Array<{ key: DagView; label: string; title: string }> = [
  { key: "spine", label: "Spine", title: "Pipeline stages, size ∝ count — orient at a glance" },
  { key: "graph", label: "Graph", title: "The lineage as a laid-out graph; switch layout, apply a lens" },
  { key: "cone", label: "Cone", title: "Where did this come from — the collapsed lineage chain" },
  { key: "map", label: "Map", title: "The whole graph; the selection's lineage is lit" },
  { key: "scale", label: "Scale", title: "Group the lineage at scale — layers collapse to totals, drill to individual runs; click to trace" },
  { key: "branch", label: "Branch", title: "The git-style branch/commit tree + A/B compare + merge" },
];

const LENS_DEFS: Array<{ key: DagLens; label: string; title: string }> = [
  { key: "trace", label: "Trace", title: "Upstream sources + downstream consumers" },
  { key: "impact", label: "Impact", title: "Downstream-only: what goes stale if this changes" },
  { key: "diff", label: "Diff", title: "Common ancestry of two+ artifacts" },
  { key: "filter", label: "Filter", title: "Highlight one kind across the graph" },
  { key: "health", label: "Health", title: "Status per node — failed / running / stale" },
  { key: "versions", label: "Versions", title: "Versions on the DAG, a timeline, and the DB tree" },
];

// Spine geometry
const SPINE_W = 240;
const SPINE_H = 120;
const radiusFor = (count: number): number => Math.max(4.5, Math.min(26, 4 + Math.sqrt(count) * 1.4));

/** The set of filter kinds offered, derived from the graph's actual types. */
function filterKindOptions(graph: Graph): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const n of graph.nodes) {
    const t = n.type ?? "node";
    if (isTransform(t)) continue;
    if (!seen.has(t)) {
      seen.add(t);
      ordered.push(t);
    }
  }
  return ordered.slice(0, 8);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function ThreadV5DagExplorer({
  nodes,
  edges,
  selectedId,
  onSelect,
  onActivate,
  view,
  onViewChange,
  compact = false,
  lens,
  onLensChange,
  className,
  onExpand,
  branchHistory,
  commits,
}: ThreadV5DagExplorerProps) {
  const reactId = useId();

  // ── The graph + index, built once from props (pure). ──
  const graph = useMemo(
    () => fromProvenanceGraph({ nodes: nodes as never, edges: edges as never }),
    [nodes, edges],
  );
  const index = useMemo(() => buildIndex(graph), [graph]);

  // ── The SINGLE selection model — the explorer's OWN root. ──
  // `selectedId` SEEDS the root; after the first in-DAG re-root the explorer
  // self-manages (`hasReRooted`) so a stale controlled `selectedId` can't yank
  // the root back out from under the user mid-exploration (the navigation bug).
  const defaultRoot = useMemo(() => pickDefaultRoot(graph, index), [graph, index]);
  const [internalSel, setInternalSel] = useState<string | null>(null);
  const [hasReRooted, setHasReRooted] = useState(false);
  const rootId = useMemo(() => {
    // Once the user has re-rooted in-DAG, the internal selection is authoritative.
    const want = hasReRooted ? internalSel : (selectedId ?? internalSel);
    if (want && index.nodeById.has(want)) return want;
    return defaultRoot;
  }, [selectedId, internalSel, hasReRooted, index, defaultRoot]);

  // ── Cone-only fan-expansion state (declared before reRoot uses it). ──
  const [expandedFans, setExpandedFans] = useState<ReadonlySet<string>>(() => new Set());

  // ── Diff lens picks — N artifacts whose common ancestry we intersect. ──
  const [diffPicks, setDiffPicks] = useState<string[]>([]);

  // A SINGLE CLICK re-roots the explorer's own cone/graph and STAYS in the DAG.
  // It fires the PURE, non-navigating `onSelect` so a host can mirror the
  // selection, but it never navigates — `hasReRooted` then makes the internal
  // root authoritative so a controlled `selectedId` can't override it.
  const reRoot = useCallback(
    (id: string) => {
      setExpandedFans(new Set());
      setInternalSel(id);
      setHasReRooted(true);
      onSelect?.(id);
    },
    [onSelect],
  );

  // The DELIBERATE jump — double-click or the explicit "open row ↗" affordance.
  // This is the only path allowed to navigate the host away from the DAG. It
  // also re-roots first so the selection the user opened is the lit one.
  const activate = useCallback(
    (id: string) => {
      reRoot(id);
      onActivate?.(id);
    },
    [reRoot, onActivate],
  );

  // ── View state. compact defaults to 'spine', full to 'cone' (the calm
  //    default; Graph stays the "explore everything" mode). ──
  const defaultView: DagView = compact ? "spine" : "cone";
  const [internalView, setInternalView] = useState<DagView>(defaultView);
  const activeView = view ?? internalView;
  const setView = useCallback(
    (v: DagView) => {
      setInternalView(v);
      onViewChange?.(v);
    },
    [onViewChange],
  );

  // ── Lens state (graph/cone modes). ──
  const [internalLens, setInternalLens] = useState<DagLens>("trace");
  const activeLens = lens ?? internalLens;
  const setLens = useCallback(
    (l: DagLens) => {
      setInternalLens(l);
      onLensChange?.(l);
    },
    [onLensChange],
  );

  // ── Layout mode (graph/map). Switching ANIMATES via the morph helper. ──
  const [layoutMode, setLayoutMode] = useState<LayoutMode>("tb");

  // ── Trace direction (up/down/both) — the cone/graph trace toggle. ──
  const [traceDir, setTraceDir] = useState<TraceDirection>("both");

  // ── Downstream toggle (cone view). ──
  const [showDownstream, setShowDownstream] = useState(false);

  // ── "Show full graph" toggle (graph/map). Off by default: nodes NOT on the
  //    selected node's lineage are hidden so the field stays legible; the toggle
  //    reveals the whole field (the disconnected flight-modes etc.). ──
  const [showFullGraph, setShowFullGraph] = useState(false);

  // ── "Advanced ⋯" disclosure — the calm-by-default toolbar tucks the power
  //    controls (layout switch, trace direction, time-travel, export/copy-link/
  //    rebuild, version-hash chips) behind one click. ──
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // ── Filter lens kind. ──
  const filterKinds = useMemo(() => filterKindOptions(graph), [graph]);
  const [filterKind, setFilterKind] = useState<string>("");
  useEffect(() => {
    if (!filterKind && filterKinds.length > 0) setFilterKind(filterKinds[0]!);
  }, [filterKind, filterKinds]);

  // ── Versions sub-view (on-graph / db / artifact). ──
  const [verView, setVerView] = useState<VerView>("graph");

  // ── Time-travel scrubber stop index. ──
  const ttModel = useMemo(() => timeTravel(graph, index), [graph, index]);
  const [timeIdx, setTimeIdx] = useState<number>(() => Math.max(0, ttModel.count - 1));
  useEffect(() => {
    // Reset to "present" when the graph changes.
    setTimeIdx(Math.max(0, ttModel.count - 1));
  }, [ttModel]);
  const isPresent = timeIdx >= ttModel.count - 1;
  const revealed = useMemo(() => ttModel.revealedAt(timeIdx), [ttModel, timeIdx]);

  // ── Search query + aux button transient confirmations. ──
  const [searchQ, setSearchQ] = useState("");
  const [copied, setCopied] = useState(false);
  const [exported, setExported] = useState(false);
  const [showRebuild, setShowRebuild] = useState(false);
  const [selRun, setSelRun] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exportTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
      if (exportTimer.current) clearTimeout(exportTimer.current);
    },
    [],
  );

  // ── Derived view models. ──
  const rollup = useMemo(() => buildRollup(graph, index, rootId), [graph, index, rootId]);
  const coneDownstream = activeLens === "impact" ? true : showDownstream;
  const cone = useMemo(
    () => buildCone(index, rootId, coneDownstream, expandedFans),
    [index, rootId, coneDownstream, expandedFans],
  );
  const mapModel = useMemo(() => buildMap(graph, index, rootId), [graph, index, rootId]);
  const scaleModel = useMemo(() => buildRealModel(graph, index), [graph, index]);

  // ── The lens result — drives node/edge roles in the GRAPH view. ──
  const lensResult = useMemo(() => {
    const lensMode: DagLensMode =
      activeLens === "health" || activeLens === "versions" ? "trace" : (activeLens as DagLensMode);
    const roots =
      activeLens === "diff" ? (diffPicks.length > 0 ? diffPicks : [rootId]) : [rootId];
    return applyLens(index, graph.edges, {
      mode: lensMode,
      roots,
      traceDirection: traceDir,
      filterKind,
    });
  }, [index, graph.edges, activeLens, diffPicks, rootId, traceDir, filterKind]);

  // ── The on-lineage node set (graph). Off-lineage nodes (role 'off') are HIDDEN
  //    by default to keep the field legible; "show full graph" reveals them. Diff
  //    picks always stay visible so a pick can't vanish. When the full graph is
  //    shown, every node is visible. ──
  const visibleNodeIds = useMemo(() => {
    if (showFullGraph) return null; // null = show everything
    const vis = new Set<string>();
    for (const [id, role] of lensResult.roleByNode) if (role !== "off") vis.add(id);
    for (const id of diffPicks) vis.add(id);
    vis.add(rootId);
    return vis;
  }, [showFullGraph, lensResult.roleByNode, diffPicks, rootId]);

  // How many nodes the lineage hides (drives the "show full graph" affordance).
  const hiddenCount = visibleNodeIds ? graph.nodes.length - visibleNodeIds.size : 0;

  // ── Health summary (Health lens readout). ──
  const health = useMemo(() => healthSummary(graph), [graph]);

  // ── The TARGET layout positions for the GRAPH view (the morph tweens to it). ──
  //    Computed ONLY while the graph view is actually showing: layout()+deoverlap()
  //    is the single most expensive computation in the workbench, and every other
  //    view (spine/cone/map/scale/branch) has its own model and never reads these
  //    positions. Computing it eagerly on mount froze the run-table surface for
  //    60-90 s on 4k-node projects (FXC-9009) — the compact tile defaults to
  //    spine and never rendered the result. An empty target is safe here:
  //    interpolatePositions treats ids missing from `from` as "enter at target",
  //    so the morph still glides when the user opens the graph view.
  const targetPositions = useMemo(
    () =>
      activeView === "graph"
        ? computeLayout(graph.nodes as never, graph.edges as never, layoutMode)
        : {},
    [graph.nodes, graph.edges, layoutMode, activeView],
  );

  // ── The ANIMATED positions — what the GRAPH view actually paints. The morph
  //    tweens from the current rendered positions to `targetPositions` whenever
  //    the layout mode (or graph) changes, so nodes GLIDE between arrangements. ──
  const [renderedPositions, setRenderedPositions] = useState<Positions>(targetPositions);
  const morphRef = useRef<{ cancel(): void } | null>(null);
  const positionsRef = useRef<Positions>(targetPositions);
  positionsRef.current = renderedPositions;

  useEffect(() => {
    if (activeView !== "graph") return;
    // Cancel any in-flight morph, then tween from where we are to the new target.
    morphRef.current?.cancel();
    const from = positionsRef.current;
    morphRef.current = createLayoutMorph(from, targetPositions, {
      duration: 460,
      onFrame: (p) => setRenderedPositions(p),
    });
    return () => morphRef.current?.cancel();
  }, [activeView, targetPositions]);

  const toggleFan = useCallback((fanId: string) => {
    setExpandedFans((prev) => {
      const next = new Set(prev);
      if (next.has(fanId)) next.delete(fanId);
      else next.add(fanId);
      return next;
    });
  }, []);

  // ── In the diff lens, a single click toggles the node in the pick set; in
  //    every other lens, a single click RE-ROOTS (and stays in the DAG). A
  //    DOUBLE-CLICK is the deliberate jump (onActivate). ──
  const onGraphNodeClick = useCallback(
    (id: string) => {
      if (activeLens === "diff") {
        setDiffPicks((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
      } else {
        reRoot(id);
      }
    },
    [activeLens, reRoot],
  );

  // Double-click → the deliberate "open this row in the table" jump. (Diff lens
  // has no row to open, so it stays a pick-toggle there.)
  const onGraphNodeActivate = useCallback(
    (id: string) => {
      if (activeLens !== "diff") activate(id);
    },
    [activeLens, activate],
  );

  // ── Deep-link copy state (shareable URL hash for the exact view). ──
  const deepLinkState = useMemo(
    () => ({ selectedId: rootId, view: activeView, lens: activeLens, direction: traceDir }),
    [rootId, activeView, activeLens, traceDir],
  );

  // Keep the URL hash in sync so the current view is shareable (full only).
  useEffect(() => {
    if (compact || typeof window === "undefined" || !window.history?.replaceState) return;
    const nh = buildDeepLinkHash(deepLinkState);
    if (window.location.hash !== nh) {
      try {
        window.history.replaceState(null, "", nh);
      } catch {
        /* hash sync best-effort */
      }
    }
  }, [compact, deepLinkState]);

  // Deep-link restore on mount (full only): adopt sel/view/lens/dir from the hash.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (compact || restoredRef.current || typeof window === "undefined") return;
    restoredRef.current = true;
    const patch = parseDeepLinkHash(window.location.hash);
    if (patch.selectedId && index.nodeById.has(patch.selectedId)) {
      // A restored deep-link root is a user-intended root → make it authoritative
      // so a controlled `selectedId` prop can't override the shared view.
      setInternalSel(patch.selectedId);
      setHasReRooted(true);
    }
    if (patch.view) setInternalView(patch.view as DagView);
    if (patch.lens) setInternalLens(patch.lens as DagLens);
    if (patch.direction) setTraceDir(patch.direction);
  }, [compact, index]);

  const empty = graph.nodes.length === 0;

  // ───────────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────────

  // Which views show the layout switcher (the laid-out surfaces).
  const showLayoutBar = !compact && (activeView === "graph" || activeView === "map");
  // Which views show the lens toolbar.
  const showLensBar = !compact && (activeView === "graph" || activeView === "cone");

  return (
    <div
      className={`v5-dag-explorer${compact ? " is-compact" : ""}${className ? ` ${className}` : ""}`}
      data-testid="v5-dag-explorer"
      data-view={activeView}
      data-lens={activeLens}
      data-layout={layoutMode}
      data-compact={compact ? "true" : "false"}
    >
      {/* ── Header: view switch (always) + expand (compact) ── */}
      <div className="v5-dag-card-head">
        <span className="v5-strip-title">
          <Icons.Dag size={13} />
          {compact ? "DAG" : "Lineage"}
        </span>
        <span className="v5-dag-mode" role="group" aria-label="DAG view">
          {VIEW_DEFS.filter((v) => !compact || v.key === "spine" || v.key === "cone").map((v) => (
            <button
              key={v.key}
              type="button"
              className={`v5-dag-mode-btn${activeView === v.key ? " is-active" : ""}`}
              title={v.title}
              aria-pressed={activeView === v.key}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
          {compact && onExpand ? (
            <button
              type="button"
              className="v5-dag-mode-btn v5-dag-expand-btn"
              title="Expand lineage"
              aria-label="Expand lineage"
              onClick={(e) => {
                e.stopPropagation();
                onExpand();
              }}
            >
              <Icons.Maximize size={13} />
            </button>
          ) : null}
        </span>
      </div>

      {/* ── Lens toolbar — graph/cone analysis modes. The calm default: the lens
            row + its contextual sub-controls (filter kinds, cone downstream).
            Power controls (trace direction, layout, time-travel, export) live in
            the Advanced disclosure below. ── */}
      {showLensBar ? (
        <div className="v5-dag-lens-bar" role="group" aria-label="Cone lens">
          {LENS_DEFS.map((l) => (
            <button
              key={l.key}
              type="button"
              className={`v5-dag-lens-btn${activeLens === l.key ? " is-active" : ""}`}
              title={l.title}
              aria-pressed={activeLens === l.key}
              onClick={() => setLens(l.key)}
            >
              {l.label}
            </button>
          ))}
          {activeLens === "filter" && filterKinds.length > 0 ? (
            <span className="v5-dag-filter-kinds" role="group" aria-label="Filter kind">
              {filterKinds.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`v5-dag-filter-kind-btn${filterKind === k ? " is-active" : ""}`}
                  aria-pressed={filterKind === k}
                  onClick={() => setFilterKind(k)}
                >
                  {k}
                </button>
              ))}
            </span>
          ) : null}
          {(activeLens === "trace" || activeLens === "impact") && activeView === "cone" ? (
            <button
              type="button"
              className={`v5-text-button v5-dag-down-toggle${coneDownstream ? " is-active" : ""}`}
              aria-pressed={coneDownstream}
              disabled={activeLens === "impact"}
              title={
                activeLens === "impact"
                  ? "Impact is downstream-only"
                  : coneDownstream
                    ? "Hide the downstream cone"
                    : "Add the downstream cone (consumers)"
              }
              onClick={() => setShowDownstream((d) => !d)}
            >
              {coneDownstream ? "↓ downstream on" : "↓ add downstream"}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* ── Search row + the single "Advanced ⋯" disclosure (full only). The
            default toolbar stays two calm rows; power is one click away. ── */}
      {!compact && activeView !== "branch" && activeView !== "scale" ? (
        <div className="v5-dag-aux-row">
          <DagSearchBox
            graph={graph}
            query={searchQ}
            onQueryChange={setSearchQ}
            onJump={(id) => {
              reRoot(id);
              setSearchQ("");
            }}
          />
          <button
            type="button"
            className={`v5-text-button v5-dag-advanced-toggle${advancedOpen ? " is-active" : ""}`}
            data-testid="v5-dag-advanced-toggle"
            aria-expanded={advancedOpen}
            aria-controls={`${reactId}-advanced`}
            title="Layout, trace direction, time-travel, export and rebuild controls"
            onClick={() => setAdvancedOpen((s) => !s)}
          >
            Advanced ⋯
          </button>
        </div>
      ) : null}

      {/* ── Advanced disclosure — the power controls, tucked away by default. ── */}
      {!compact && activeView !== "branch" && activeView !== "scale" && advancedOpen ? (
        <div className="v5-dag-advanced" id={`${reactId}-advanced`} data-testid="v5-dag-advanced">
          {showLayoutBar ? (
            <div className="v5-dag-control-row">
              <div className="v5-dag-layout-bar" role="group" aria-label="Graph layout">
                <span className="v5-dag-control-label">layout</span>
                <span className="v5-dag-layout-pills">
                  {LAYOUT_MODES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`v5-dag-layout-btn${layoutMode === m ? " is-active" : ""}`}
                      data-mode={m}
                      aria-pressed={layoutMode === m}
                      title={`Layout: ${m}`}
                      onClick={() => setLayoutMode(m)}
                    >
                      {LAYOUT_LABELS[m]}
                    </button>
                  ))}
                </span>
              </div>
              {activeView === "graph" ? (
                <div className={`v5-dag-timetravel-wrap${isPresent ? "" : " is-past"}`}>
                  <TimeTravelScrubber
                    graph={graph}
                    index={index}
                    value={timeIdx}
                    onChange={(i) => setTimeIdx(i)}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {showLensBar && (activeLens === "trace" || activeLens === "diff" || activeView === "graph") ? (
            <div className="v5-dag-control-row">
              <span className="v5-dag-control-label">trace</span>
              <TraceDirectionToggle direction={traceDir} onChange={setTraceDir} />
            </div>
          ) : null}
          <div className="v5-dag-aux-actions">
            <CopyLinkButton
              state={deepLinkState}
              copied={copied}
              onCopied={() => {
                setCopied(true);
                if (copyTimer.current) clearTimeout(copyTimer.current);
                copyTimer.current = setTimeout(() => setCopied(false), 1600);
              }}
            />
            <DownloadConeButton
              graph={graph}
              index={index}
              rootId={rootId}
              direction={traceDir === "down" ? "down" : "up"}
              done={exported}
              onDone={() => {
                setExported(true);
                if (exportTimer.current) clearTimeout(exportTimer.current);
                exportTimer.current = setTimeout(() => setExported(false), 1600);
              }}
            />
            <button
              type="button"
              className={`v5-text-button v5-dag-rebuild-toggle${showRebuild ? " is-active" : ""}`}
              aria-pressed={showRebuild}
              title="Turn the upstream cone into a reproducible run plan"
              onClick={() => setShowRebuild((s) => !s)}
            >
              ⟲ rebuild from here
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Rebuild-from-here run plan (full only, when toggled). ── */}
      {!compact && showRebuild && !empty ? (
        <div className="v5-dag-rebuild-host">
          <RebuildPlanPanel index={index} rootId={rootId} />
        </div>
      ) : null}

      {/* ── The view body ── */}
      <div className="v5-dag-explorer-body">
        {empty ? (
          <div className="v5-dag-empty">
            <Icons.Dag size={28} />
            <p className="v5-muted">No provenance lineage for this project yet.</p>
          </div>
        ) : activeView === "spine" ? (
          <SpineView rollup={rollup} onSelectStageNode={reRoot} index={index} rootId={rootId} />
        ) : activeView === "graph" ? (
          <GraphView
            graph={graph}
            positions={renderedPositions}
            roleByNode={lensResult.roleByNode}
            litEdges={lensResult.litEdges}
            rootId={rootId}
            lens={activeLens}
            diffPicks={diffPicks}
            revealed={revealed}
            isPresent={isPresent}
            verView={verView}
            visibleNodeIds={visibleNodeIds}
            showFullGraph={showFullGraph}
            hiddenCount={hiddenCount}
            onToggleFullGraph={() => setShowFullGraph((s) => !s)}
            onNodeClick={onGraphNodeClick}
            onNodeActivate={onGraphNodeActivate}
          />
        ) : activeView === "cone" ? (
          <ConeView
            cone={cone}
            rootId={rootId}
            lens={activeLens}
            onReRoot={reRoot}
            onActivate={activate}
            onToggleFan={toggleFan}
            idPrefix={reactId}
          />
        ) : activeView === "scale" ? (
          <DagScaleView model={scaleModel} onPickReal={reRoot} onActivate={activate} />
        ) : activeView === "branch" ? (
          <DagBranchPanel
            branches={branchHistory}
            commits={commits}
            nodes={graph.nodes as never}
            rootId={rootId}
          />
        ) : (
          <MapView
            model={mapModel}
            showFullGraph={showFullGraph}
            hiddenCount={mapModel.points.length - mapModel.litCount}
            onToggleFullGraph={() => setShowFullGraph((s) => !s)}
            onSelect={reRoot}
            onActivate={activate}
          />
        )}
      </div>

      {/* ── Versions / Health / Diff side panels for the GRAPH view (full only). ── */}
      {!compact && activeView === "graph" && activeLens === "versions" && !empty ? (
        <div className="v5-dag-side-panel">
          <DagVersionsPanel
            artifactId={rootId}
            artifactLabel={index.nodeById.get(rootId)?.label ?? rootId}
            view={verView}
            onViewChange={setVerView}
          />
        </div>
      ) : null}
      {!compact && activeView === "graph" && activeLens === "health" && !empty ? (
        <div className="v5-dag-side-panel">
          <HealthPanel graph={graph} />
        </div>
      ) : null}

      {/* ── Run-grid drill-in (full only): the run-fan as a faceted grid. ── */}
      {!compact && activeView === "graph" && activeLens !== "versions" && !empty ? (
        <RunGridDrillIn
          graph={graph}
          index={index}
          selectedRunId={selRun}
          onSelectRun={setSelRun}
        />
      ) : null}

      {/* ── Footer caption ── */}
      <div className="v5-dag-explorer-foot">
        <span className="v5-muted">{footCaption(activeView, activeLens, empty, cone, coneDownstream, rollup, mapModel, lensResult, health)}</span>
        {!compact && activeView === "graph" && activeLens === "diff" ? (
          <span className="v5-dag-diff-picks">
            {diffPicks.length === 0
              ? "click nodes to add to the diff set"
              : `${diffPicks.length} picked — ${lensResult.summary.note}`}
          </span>
        ) : null}
      </div>
    </div>
  );
}

const LAYOUT_LABELS: Record<LayoutMode, string> = {
  tb: "↓ T–B",
  bt: "↑ B–T",
  lr: "→ L–R",
  rl: "← R–L",
  force: "✳ force",
  radial: "◎ radial",
};

function footCaption(
  view: DagView,
  lens: DagLens,
  empty: boolean,
  cone: ConeModel,
  coneDownstream: boolean,
  rollup: Rollup,
  mapModel: MapModel,
  lensResult: ReturnType<typeof applyLens>,
  health: ReturnType<typeof healthSummary>,
): string {
  if (empty) return "no lineage yet";
  if (view === "cone") {
    return (
      `${cone.root?.label ?? "—"} · ${fmt(cone.totalUp)} upstream` +
      (coneDownstream ? ` · ${fmt(cone.totalDown)} downstream` : "")
    );
  }
  if (view === "map") {
    return `${fmt(rollup.totalNodes)} nodes · ${fmt(mapModel.litCount)} in lineage`;
  }
  if (view === "graph") {
    if (lens === "health") {
      return health.healthy
        ? `all ${fmt(health.total)} artifacts healthy`
        : `${health.failed} failed · ${health.stale} stale · ${health.running} running`;
    }
    return lensResult.summary.note;
  }
  if (view === "branch") return "branch / commit history — pick two commits to diff";
  if (view === "scale") return `${fmt(rollup.totalNodes)} nodes · grouped lineage — zoom to drill layers → groups → runs · click to trace`;
  return `${rollup.stages.length} stages · ${fmt(rollup.totalNodes)} nodes` + (rollup.runCount ? ` · ${fmt(rollup.runCount)} runs` : "");
}

// ─────────────────────────────────────────────────────────────────────────────
// SPINE view — the stage-rollup orienter.
// ─────────────────────────────────────────────────────────────────────────────

function representativeOfStage(index: GraphIndex, rootId: string, stageIdx: number): string | null {
  const lineage = new Set<string>([rootId]);
  if (index.nodeById.has(rootId)) {
    for (const id of ancestors(index, rootId, 32).ids) lineage.add(id);
    for (const id of descendants(index, rootId, 32).ids) lineage.add(id);
  }
  let fallback: string | null = null;
  for (const [id, node] of index.nodeById) {
    if (isTransform(node.type ?? "")) continue;
    if (stageOf(node.type ?? "") !== stageIdx) continue;
    if (lineage.has(id)) return id;
    if (fallback === null) fallback = id;
  }
  return fallback;
}

function SpineView({
  rollup,
  onSelectStageNode,
  index,
  rootId,
}: {
  rollup: Rollup;
  onSelectStageNode: (id: string) => void;
  index: GraphIndex;
  rootId: string;
}) {
  const spineY = SPINE_H / 2;
  const cols = useMemo(() => {
    const n = rollup.stages.length;
    if (n === 0) return [] as number[];
    const x0 = 26;
    const x1 = SPINE_W - 26;
    return rollup.stages.map((_, i) => (n === 1 ? SPINE_W / 2 : x0 + (i / (n - 1)) * (x1 - x0)));
  }, [rollup.stages]);

  const selectStage = (stageIdx: number) => {
    const rep = representativeOfStage(index, rootId, stageIdx);
    if (rep) onSelectStageNode(rep);
  };

  return (
    <svg
      className="v5-dag-mini-svg"
      viewBox={`0 0 ${SPINE_W} ${SPINE_H}`}
      preserveAspectRatio="xMidYMid meet"
      role="group"
      aria-label="Pipeline stage spine"
    >
      {rollup.stages.slice(1).map((s, i) => (
        <line key={`c-${s.idx}`} className="is-lit" x1={cols[i]} y1={spineY} x2={cols[i + 1]} y2={spineY} />
      ))}
      {rollup.stages.map((s, i) => {
        const r = radiusFor(s.count);
        return (
          <g
            key={`s-${s.idx}`}
            className="v5-dag-spine-stage"
            role="button"
            tabIndex={0}
            aria-label={`${s.label}: ${fmt(s.count)} nodes`}
            onClick={() => selectStage(s.idx)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                selectStage(s.idx);
              }
            }}
            style={{ cursor: "pointer" }}
          >
            {s.here ? <circle className="v5-dag-here-ring" cx={cols[i]} cy={spineY} r={r + 3.5} /> : null}
            <circle className={`${TONE_CLASS[s.tone]}${s.here ? " is-lit" : ""}`} cx={cols[i]} cy={spineY} r={r} />
            <text
              className="v5-dag-mini-count"
              x={cols[i]}
              y={Math.min(spineY + r + 11, SPINE_H - 3)}
              textAnchor="middle"
            >
              {fmt(s.count)} {s.label}
            </text>
            {s.gap ? (
              <text className="v5-dag-gap" x={cols[i]! + r - 1} y={spineY - r + 2} textAnchor="middle">
                ⚠
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GRAPH view — the design's PRIMARY animated layout view. Nodes are HTML boxes
// positioned by the (animating) layout positions; edges are CSS-rotated bars
// between them. The active lens colours each node by role; time-travel hides
// not-yet-built nodes; the versions lens stamps a vN badge on each node.
// ─────────────────────────────────────────────────────────────────────────────

/** A role → CSS class for node/edge tinting. Tokens live in thread-v5.css. */
const ROLE_CLASS: Record<NodeRole, string> = {
  sel: "is-sel",
  selpick: "is-selpick",
  up: "is-up",
  down: "is-down",
  shared: "is-shared",
  partial: "is-partial",
  match: "is-match",
  off: "is-off",
};

function GraphView({
  graph,
  positions,
  roleByNode,
  litEdges,
  rootId,
  lens,
  diffPicks,
  revealed,
  isPresent,
  verView,
  visibleNodeIds,
  showFullGraph,
  hiddenCount,
  onToggleFullGraph,
  onNodeClick,
  onNodeActivate,
}: {
  graph: Graph;
  positions: Positions;
  roleByNode: Map<string, NodeRole>;
  litEdges: ReturnType<typeof applyLens>["litEdges"];
  rootId: string;
  lens: DagLens;
  diffPicks: string[];
  revealed: Set<string>;
  isPresent: boolean;
  verView: VerView;
  /** null = show every node; otherwise only these ids render (off-lineage hidden). */
  visibleNodeIds: Set<string> | null;
  showFullGraph: boolean;
  hiddenCount: number;
  onToggleFullGraph: () => void;
  onNodeClick: (id: string) => void;
  onNodeActivate: (id: string) => void;
}) {
  // Edge-role lookup so a lit edge paints its trace colour; others stay faint.
  const litEdgeKeys = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of litEdges) m.set(`${e.source}->${e.target}`, e.role);
    return m;
  }, [litEdges]);

  // For the versions lens (on-graph sub-view) the nodes carry a vN badge.
  const showVerBadge = lens === "versions" && verView === "graph";
  const pickIndex = useMemo(() => {
    const m = new Map<string, number>();
    diffPicks.forEach((id, i) => m.set(id, i));
    return m;
  }, [diffPicks]);

  const isVisible = (id: string): boolean => visibleNodeIds === null || visibleNodeIds.has(id);

  // FIT-TO-FILL: rescale the VISIBLE positions so their bbox fills the stage in
  // both axes (recomputed each render → re-centres on resize, layout flip, and
  // re-root). When everything is hidden but the root, the fit centres it.
  const fit = useMemo(() => {
    const pts: Array<{ x: number; y: number }> = [];
    for (const n of graph.nodes) {
      const p = positions[n.id];
      if (p && isVisible(n.id)) pts.push(p);
    }
    return fitNormalized(pts, 0.07);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.nodes, positions, visibleNodeIds]);

  const X = (x: number) => `${(fit.x(x) * 100).toFixed(3)}%`;
  const Y = (y: number) => `${(fit.y(y) * 100).toFixed(3)}%`;

  return (
    <div className="v5-dag-graph" data-testid="v5-dag-graph" data-root={rootId} data-lens={lens} data-full={showFullGraph ? "true" : "false"}>
      {/* "show full graph" toggle — off-lineage nodes are hidden by default. */}
      {hiddenCount > 0 || showFullGraph ? (
        <button
          type="button"
          className={`v5-text-button v5-dag-fullgraph-toggle${showFullGraph ? " is-active" : ""}`}
          data-testid="v5-dag-fullgraph-toggle"
          aria-pressed={showFullGraph}
          title={showFullGraph ? "Show only the selection's lineage" : "Reveal the whole graph, including off-lineage nodes"}
          onClick={onToggleFullGraph}
        >
          {showFullGraph ? "↺ lineage only" : `⊕ show full graph${hiddenCount > 0 ? ` (+${fmt(hiddenCount)})` : ""}`}
        </button>
      ) : null}
      <div className="v5-dag-graph-stage">
        {/* Edges as smooth SVG bezier paths, drawn first (under the nodes). The viewBox is the
            same 0..100 normalised space as the %-positioned nodes; preserveAspectRatio="none"
            stretches it to the stage and non-scaling-stroke keeps the line weight constant.
            Lit edges are drawn last so they sit above the faint off-lineage ones. */}
        {(() => {
          const segs: Array<{ i: number; d: string; role: string; hidden: boolean; lit: boolean }> = [];
          for (let i = 0; i < graph.edges.length; i++) {
            const e = graph.edges[i];
            if (!e) continue;
            const a = positions[e.source];
            const b = positions[e.target];
            if (!a || !b) continue;
            if (!isVisible(e.source) || !isVisible(e.target)) continue;
            const ax = fit.x(a.x) * 100;
            const ay = fit.y(a.y) * 100;
            const bx = fit.x(b.x) * 100;
            const by = fit.y(b.y) * 100;
            // S-curve along the dominant flow axis (horizontal for L→R/R→L layouts, vertical for T↓B).
            const horiz = Math.abs(bx - ax) >= Math.abs(by - ay);
            const mx = ((ax + bx) / 2).toFixed(2);
            const my = ((ay + by) / 2).toFixed(2);
            const A = `${ax.toFixed(2)},${ay.toFixed(2)}`;
            const B = `${bx.toFixed(2)},${by.toFixed(2)}`;
            const d = horiz
              ? `M${A} C${mx},${ay.toFixed(2)} ${mx},${by.toFixed(2)} ${B}`
              : `M${A} C${ax.toFixed(2)},${my} ${bx.toFixed(2)},${my} ${B}`;
            const role = litEdgeKeys.get(`${e.source}->${e.target}`) ?? "off";
            const hidden = !isPresent && (!revealed.has(e.source) || !revealed.has(e.target));
            segs.push({ i, d, role, hidden, lit: role !== "off" });
          }
          segs.sort((p, q) => Number(p.lit) - Number(q.lit));
          return (
            <svg className="v5-dag-graph-edges" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              {segs.map((p) => (
                <path key={`edge-${p.i}`} className={`v5-dag-edge-path is-${p.role}${p.hidden ? " is-future" : ""}`} d={p.d} />
              ))}
            </svg>
          );
        })()}

        {/* Nodes. */}
        {graph.nodes.map((n) => {
          const p = positions[n.id];
          if (!p) return null;
          if (!isVisible(n.id)) return null;
          const role = roleByNode.get(n.id) ?? "off";
          const hidden = !isPresent && !revealed.has(n.id);
          const tone = STAGE_DEFS[stageOf(n.type ?? "")]!.tone;
          const isRoot = n.id === rootId;
          const pick = pickIndex.get(n.id);
          const st = lens === "health" ? statusOf(n, n.id) : null;
          const ver = showVerBadge ? deterministicVersionsFor(n.id) : null;
          return (
            <button
              key={n.id}
              type="button"
              className={
                `v5-dag-graph-node ${ROLE_CLASS[role]} ${TONE_CLASS[tone]}` +
                `${isRoot ? " is-root" : ""}${hidden ? " is-future" : ""}` +
                `${st ? ` is-status-${st}` : ""}`
              }
              data-node-id={n.id}
              data-role={role}
              data-status={st ?? undefined}
              aria-current={isRoot ? "true" : undefined}
              aria-pressed={pick !== undefined ? true : undefined}
              title={`${n.type ?? "node"} · ${n.label ?? n.id} — click to re-root, double-click to open`}
              style={{ left: X(p.x), top: Y(p.y) }}
              onClick={() => onNodeClick(n.id)}
              onDoubleClick={() => onNodeActivate(n.id)}
            >
              <span className="v5-dag-graph-node-label">{n.label ?? n.id}</span>
              <span className="v5-dag-graph-node-kind">{n.type ?? "node"}</span>
              {pick !== undefined ? <span className="v5-dag-graph-node-pick">{String.fromCharCode(65 + pick)}</span> : null}
              {ver ? (
                <span className={`v5-dag-graph-node-ver${ver.stale ? " is-stale" : ""}`}>v{ver.versions.length}</span>
              ) : null}
              {st && st !== "ok" ? <span className={`v5-dag-graph-node-glyph is-${st}`}>{STATUS_GLYPH[st]}</span> : null}
              {isRoot ? (
                <span
                  className="v5-dag-graph-node-open"
                  role="button"
                  tabIndex={-1}
                  aria-label="Open this row in the table"
                  title="Open this row in the table"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onNodeActivate(n.id);
                  }}
                >
                  ↗
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const STATUS_GLYPH: Record<string, string> = { failed: "✕", stale: "~", running: "•", ok: "" };

// ─────────────────────────────────────────────────────────────────────────────
// CONE view — the collapsed-chain reading.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * FIT-TO-FILL for flow-laid content (the cone rail): measure the inner content's
 * natural size against the outer container and return a scale that grows it to
 * fill BOTH axes (capped so it never blows up past `maxScale`, never shrinks
 * below visibility). Recomputes on resize via a ResizeObserver, so the cone
 * re-fits when the panel changes or the chain re-roots. Falls back to scale 1
 * where there's no ResizeObserver (jsdom/SSR), so tests render unscaled.
 */
function useFitScale(maxScale = 2.4): {
  outerRef: (el: HTMLDivElement | null) => void;
  innerRef: (el: HTMLDivElement | null) => void;
  scale: number;
} {
  const [scale, setScale] = useState(1);
  const outerEl = useRef<HTMLDivElement | null>(null);
  const innerEl = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  const measure = useCallback(() => {
    const outer = outerEl.current;
    const inner = innerEl.current;
    if (!outer || !inner) return;
    // Natural (unscaled) content size: divide the rendered size by the current
    // scale so the measurement is independent of the transform we applied.
    const cur = scale || 1;
    const cw = inner.scrollWidth / cur;
    const ch = inner.scrollHeight / cur;
    const aw = outer.clientWidth;
    const ah = outer.clientHeight;
    if (cw < 1 || ch < 1 || aw < 1 || ah < 1) return;
    const next = Math.min(maxScale, Math.max(0.4, Math.min(aw / cw, ah / ch)));
    setScale((prev) => (Math.abs(prev - next) > 0.01 ? next : prev));
  }, [maxScale, scale]);

  const attach = useCallback(() => {
    if (typeof ResizeObserver === "undefined") return;
    roRef.current?.disconnect();
    const ro = new ResizeObserver(() => measure());
    if (outerEl.current) ro.observe(outerEl.current);
    if (innerEl.current) ro.observe(innerEl.current);
    roRef.current = ro;
    measure();
  }, [measure]);

  useEffect(() => () => roRef.current?.disconnect(), []);

  const outerRef = useCallback(
    (el: HTMLDivElement | null) => {
      outerEl.current = el;
      attach();
    },
    [attach],
  );
  const innerRef = useCallback(
    (el: HTMLDivElement | null) => {
      innerEl.current = el;
      attach();
    },
    [attach],
  );

  return { outerRef, innerRef, scale };
}

function ConeView({
  cone,
  rootId,
  lens,
  onReRoot,
  onActivate,
  onToggleFan,
  idPrefix,
}: {
  cone: ConeModel;
  rootId: string;
  lens: DagLens;
  onReRoot: (id: string) => void;
  onActivate: (id: string) => void;
  onToggleFan: (fanId: string) => void;
  idPrefix: string;
}) {
  const { outerRef, innerRef, scale } = useFitScale();

  if (!cone.root) {
    return (
      <div className="v5-dag-empty">
        <p className="v5-muted">Select an artifact to trace its lineage.</p>
      </div>
    );
  }

  const upLayers = [...cone.up].reverse();

  const stubNote =
    lens === "diff"
      ? "Diff lens — pick two artifacts on the Graph view to find their common ancestry."
      : lens === "filter"
        ? "Filter lens — switch to the Graph view to highlight one kind across the field."
        : null;

  return (
    <div className="v5-dag-cone" data-testid="v5-dag-cone" data-root={rootId} ref={outerRef}>
      {stubNote ? (
        <div className="v5-dag-lens-note" role="note">
          {stubNote}
        </div>
      ) : null}

      <div
        className="v5-dag-cone-rail"
        role="list"
        aria-label="Lineage cone"
        ref={innerRef}
        style={{ transform: scale !== 1 ? `scale(${scale.toFixed(3)})` : undefined }}
      >
        {lens !== "impact"
          ? upLayers.map((layer) => (
              <ConeColumn
                key={`up-${layer.depth}`}
                layer={layer}
                lane="up"
                onReRoot={onReRoot}
                onActivate={onActivate}
                onToggleFan={onToggleFan}
                idPrefix={idPrefix}
              />
            ))
          : null}

        <div className="v5-dag-cone-col is-root" role="listitem">
          <button
            type="button"
            className="v5-dag-cone-node is-root"
            data-testid="v5-dag-cone-root"
            data-node-id={cone.root.id}
            title={`${cone.root.type} · ${cone.root.label} — double-click to open the row`}
            aria-current="true"
            onDoubleClick={() => onActivate(cone.root!.id)}
          >
            <span className="v5-dag-cone-node-kind">{cone.root.type}</span>
            <span className="v5-dag-cone-node-label">{cone.root.label}</span>
            <span
              className="v5-dag-cone-node-open"
              role="button"
              tabIndex={-1}
              aria-label="Open this row in the table"
              title="Open this row in the table"
              onClick={(ev) => {
                ev.stopPropagation();
                onActivate(cone.root!.id);
              }}
            >
              ↗
            </span>
          </button>
        </div>

        {cone.down.map((layer) => (
          <ConeColumn
            key={`down-${layer.depth}`}
            layer={layer}
            lane="down"
            onReRoot={onReRoot}
            onActivate={onActivate}
            onToggleFan={onToggleFan}
            idPrefix={idPrefix}
          />
        ))}
      </div>
    </div>
  );
}

function ConeColumn({
  layer,
  lane,
  onReRoot,
  onActivate,
  onToggleFan,
  idPrefix,
}: {
  layer: ConeLayer;
  lane: "up" | "down";
  onReRoot: (id: string) => void;
  onActivate: (id: string) => void;
  onToggleFan: (fanId: string) => void;
  idPrefix: string;
}) {
  return (
    <div className={`v5-dag-cone-col is-${lane}`} role="listitem">
      <span className="v5-dag-cone-col-rule" aria-hidden="true" />
      {layer.nodes.map((node) =>
        node.kind === "fan" ? (
          <button
            key={`${idPrefix}-${node.id}`}
            type="button"
            className="v5-dag-cone-node is-fan"
            data-testid="v5-dag-cone-fan"
            data-fan-id={node.id}
            data-count={node.count}
            title={`${node.count} ${node.type} — click to expand`}
            onClick={() => onToggleFan(node.id)}
          >
            <span className="v5-dag-cone-node-kind">{node.type}</span>
            <span className="v5-dag-cone-node-label">{node.label}</span>
            <span className="v5-dag-cone-fan-badge">×{node.count}</span>
          </button>
        ) : (
          <button
            key={`${idPrefix}-${node.id}`}
            type="button"
            className={`v5-dag-cone-node is-${lane}`}
            data-node-id={node.id}
            title={`${node.type} · ${node.label} — click to re-root, double-click to open`}
            onClick={() => onReRoot(node.id)}
            onDoubleClick={() => onActivate(node.id)}
          >
            <span className="v5-dag-cone-node-kind">{node.type}</span>
            <span className="v5-dag-cone-node-label">{node.label}</span>
          </button>
        ),
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAP view — the whole graph as a density layout; the selection's lineage lit.
// ─────────────────────────────────────────────────────────────────────────────

const MAP_W = 480;
const MAP_H = 280;
const MAP_DOT_CAP = 1200;

function MapView({
  model,
  showFullGraph,
  hiddenCount,
  onToggleFullGraph,
  onSelect,
  onActivate,
}: {
  model: MapModel;
  showFullGraph: boolean;
  hiddenCount: number;
  onToggleFullGraph: () => void;
  onSelect: (id: string) => void;
  onActivate: (id: string) => void;
}) {
  const litPoints = model.points.filter((p) => p.lit);
  // Off-lineage dots are hidden by default; "show full graph" reveals them.
  const dimPoints = showFullGraph ? model.points.filter((p) => !p.lit).slice(0, MAP_DOT_CAP) : [];
  const dimOverflow = showFullGraph ? model.points.length - litPoints.length - dimPoints.length : 0;

  // FIT-TO-FILL: rescale the drawn points' bbox to fill the viewBox in both axes.
  // When only the lineage is shown, the lit cone fills the panel; with the full
  // graph on, the whole field fits. Edges remap through the same transform.
  const fit = useMemo(() => {
    const drawn = showFullGraph ? model.points : litPoints;
    return fitNormalized(drawn, 0.06);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.points, showFullGraph]);
  const X = (x: number) => 8 + fit.x(x) * (MAP_W - 16);
  const Y = (y: number) => 8 + fit.y(y) * (MAP_H - 16);

  return (
    <div className="v5-dag-map" data-full={showFullGraph ? "true" : "false"}>
      {hiddenCount > 0 || showFullGraph ? (
        <button
          type="button"
          className={`v5-text-button v5-dag-fullgraph-toggle${showFullGraph ? " is-active" : ""}`}
          data-testid="v5-dag-fullgraph-toggle"
          aria-pressed={showFullGraph}
          title={showFullGraph ? "Show only the selection's lineage" : "Reveal the whole graph, including off-lineage nodes"}
          onClick={onToggleFullGraph}
        >
          {showFullGraph ? "↺ lineage only" : `⊕ show full graph${hiddenCount > 0 ? ` (+${fmt(hiddenCount)})` : ""}`}
        </button>
      ) : null}
      <svg
        className="v5-dag-map-svg"
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="group"
        aria-label="Full graph map"
        data-testid="v5-dag-map"
      >
        {dimPoints.map((p) => (
          <circle key={`d-${p.id}`} className="v5-dag-map-dot is-dim" cx={X(p.x)} cy={Y(p.y)} r={1.6} />
        ))}

        {model.litEdges.map((e, i) => (
          <line
            key={`le-${i}`}
            className="v5-dag-map-edge is-lit"
            x1={X(e.ax)}
            y1={Y(e.ay)}
            x2={X(e.bx)}
            y2={Y(e.by)}
          />
        ))}

        {litPoints.map((p) => (
          <g
            key={`l-${p.id}`}
            className="v5-dag-map-node"
            role="button"
            tabIndex={0}
            aria-label={p.id}
            onClick={() => onSelect(p.id)}
            onDoubleClick={() => onActivate(p.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(p.id);
              }
            }}
            style={{ cursor: "pointer" }}
          >
            {p.isRoot ? <circle className="v5-dag-here-ring" cx={X(p.x)} cy={Y(p.y)} r={6.5} /> : null}
            <circle
              className={`${TONE_CLASS[p.tone]} is-lit${p.isRoot ? " is-root" : ""}`}
              cx={X(p.x)}
              cy={Y(p.y)}
              r={p.isRoot ? 4.5 : 3}
            />
          </g>
        ))}

        {dimOverflow > 0 ? (
          <text className="v5-dag-mini-count" x={MAP_W - 10} y={MAP_H - 8} textAnchor="end">
            +{fmt(dimOverflow)} more
          </text>
        ) : null}
      </svg>
    </div>
  );
}

export default ThreadV5DagExplorer;
